import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Button,
  Box,
  Rating
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Share as ShareIcon,
  Info as InfoIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { GalleryItem, ViewMode } from './types';
import { getCategoryGradient, handleCall, handleWhatsApp, handleShare } from './utils';

interface ProductCardProps {
  item: GalleryItem;
  viewMode: ViewMode;
  isFavorite: boolean;
  onToggleFavorite: (id: number) => void;
  onProductClick: (item: GalleryItem) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  item,
  viewMode,
  isFavorite,
  onToggleFavorite,
  onProductClick
}) => {
  const navigate = useNavigate();

  const handleItemClick = () => {
    // Special handling for specific categories - navigate to dedicated pages
    if (item.category === 'Tiles') {
      navigate('/gallery/tiles');
    } else if (item.category === 'CP Fittings') {
      navigate('/gallery/cp-fittings');
    } else if (item.category === 'Bath Fittings') {
      navigate('/gallery/bath-fittings');
    } else if (item.category === 'Bath Accessories') {
      navigate('/gallery/bath-accessories');
    } else if (item.category === 'Kitchen Fittings') {
      navigate('/gallery/kitchen-fittings');
    } else if (item.category === 'Sanitary Ware') {
      navigate('/gallery/sanitary-ware');
    } else if (item.category === 'Granite') {
      navigate('/gallery/granite');
    } else {
      onProductClick(item);
    }
  };
  return (
    <Card 
      sx={{ 
        height: '100%',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '16px',
        border: '1px solid rgba(0,0,0,0.08)',
        '&:hover': {
          transform: 'translateY(-12px) scale(1.02)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          '& .card-overlay': {
            opacity: 1
          }
        },
        ...(viewMode === 'list' && {
          display: 'flex',
          flexDirection: 'row',
          height: 'auto'
        })
      }}
      onClick={handleItemClick}
    >
      {/* Badges */}
      <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 3, display: 'flex', gap: 1, flexDirection: 'column' }}>
        {item.isNew && (
          <Chip 
            label="NEW" 
            size="small" 
            sx={{ 
              backgroundColor: '#4caf50', 
              color: 'white', 
              fontWeight: 'bold',
              fontSize: '0.7rem'
            }} 
          />
        )}
        {item.isPopular && (
          <Chip 
            label="POPULAR" 
            size="small" 
            sx={{ 
              backgroundColor: '#ff5722', 
              color: 'white', 
              fontWeight: 'bold',
              fontSize: '0.7rem'
            }} 
          />
        )}
      </Box>

      {/* Favorite Button */}
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite(item.id);
        }}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 3,
          backgroundColor: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(10px)',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,1)',
            transform: 'scale(1.1)'
          },
          transition: 'all 0.3s ease'
        }}
      >
        {isFavorite ? (
          <FavoriteIcon sx={{ color: '#e91e63' }} />
        ) : (
          <FavoriteBorderIcon sx={{ color: '#666' }} />
        )}
      </IconButton>

      <CardMedia
        component="div"
        sx={{
          height: viewMode === 'grid' ? 240 : 200,
          width: viewMode === 'list' ? 300 : '100%',
          minWidth: viewMode === 'list' ? 300 : undefined,
          position: 'relative',
          background: getCategoryGradient(item.category),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '1.4rem',
          fontWeight: 'bold',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 1
          }
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
            {item.category}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Premium Quality
          </Typography>
        </Box>

        {/* Hover Overlay */}
        <Box 
          className="card-overlay"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            opacity: 0,
            transition: 'opacity 0.3s ease',
            zIndex: 2
          }}
        >
          <Button
            variant="contained"
            size="small"
            sx={{ backgroundColor: '#ffca28', color: '#1a237e' }}
            onClick={(e) => {
              e.stopPropagation();
              handleWhatsApp(item.title);
            }}
          >
            <WhatsAppIcon sx={{ mr: 1 }} /> WhatsApp
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{ color: 'white', borderColor: 'white' }}
            onClick={(e) => {
              e.stopPropagation();
              handleCall();
            }}
          >
            <PhoneIcon sx={{ mr: 1 }} /> Call
          </Button>
        </Box>
      </CardMedia>

      <CardContent sx={{ p: 3, flex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="h3" sx={{ 
            fontWeight: 'bold',
            color: '#1a237e',
            flex: 1
          }}>
            {item.title}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleShare(item);
            }}
            sx={{ ml: 1 }}
          >
            <ShareIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Rating value={item.rating} readOnly precision={0.1} size="small" />
          <Typography variant="body2" sx={{ color: '#666' }}>
            ({item.reviews} reviews)
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ 
          color: '#666',
          mb: 2,
          lineHeight: 1.6,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}>
          {item.description}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
          <Chip 
            label={item.availability}
            size="small"
            color={item.availability === 'In Stock' ? 'success' : 
                   item.availability === 'Limited Stock' ? 'warning' : 'error'}
            variant="outlined"
          />
        </Box>
        
        {/* Features List */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {item.features.slice(0, 3).map((feature, featureIndex) => (
              <Typography 
                key={featureIndex}
                variant="caption" 
                sx={{ 
                  color: '#1a237e',
                  backgroundColor: '#e8eaf6',
                  px: 1,
                  py: 0.5,
                  borderRadius: '12px',
                  fontSize: '0.7rem',
                  fontWeight: '500'
                }}
              >
                {feature}
              </Typography>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
          <Button 
            variant="contained" 
            size="small" 
            fullWidth
            sx={{ 
              backgroundColor: '#1a237e',
              borderRadius: '8px',
              '&:hover': { backgroundColor: '#3949ab' }
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleItemClick();
            }}
          >
            <InfoIcon sx={{ mr: 1, fontSize: '1rem' }} /> View Details
          </Button>
          <Button 
            variant="outlined" 
            size="small"
            sx={{ 
              borderColor: '#ffca28',
              color: '#ffca28',
              borderRadius: '8px',
              minWidth: 'auto',
              px: 2
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleWhatsApp(item.title);
            }}
          >
            <WhatsAppIcon fontSize="small" />
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
